import axios from 'axios';
import { useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { API_REQUEST_URL } from '../../../common/apiConfig';
import {
  DASHBOARD_FETCH_ORDER_REPORT_DATA_BEGIN,
  DASHBOARD_FETCH_ORDER_REPORT_DATA_SUCCESS,
  DASHBOARD_FETCH_ORDER_REPORT_DATA_FAILURE,
  DASHBOARD_FETCH_ORDER_REPORT_DATA_DISMISS_ERROR,
  ERROR_CONTACT_ADMIN,
} from './constants';

const normalizeDeliveryDateToYYYYMMDD = value => {
  if (value === null || value === undefined) return value;
  const str = String(value).trim();
  // already YYYYMMDD
  if (/^\d{8}$/.test(str)) return str;
  // convert YYYY-MM-DD -> YYYYMMDD
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str.replace(/-/g, '');
  // keep anything else unchanged to avoid side effects
  return value;
};

export function fetchOrderReportData(condition = {}) {
   return (dispatch, getState) => { // optionally you can have getState as the second argument
    dispatch({
      type: DASHBOARD_FETCH_ORDER_REPORT_DATA_BEGIN,
    });
    const isUnderMin = condition.orderStatus === 'underMin';

    const promise = new Promise((resolve, reject) => {
      const cognitoIdToken = getState().home.auth.cognitoIdToken;
      const token = `Bearer ${cognitoIdToken}`;
      // const payload = {
      //   ...condition,
      //   ...(condition.orderStatus === 'underMin' && { caseCount: condition.caseCount || 15 }),
      // };
      const payload = {
        ...condition,
        ...(isUnderMin && { caseCount: condition.caseCount || 15 }),
        ...(isUnderMin &&
          condition.deliveryDate !== undefined &&
          condition.deliveryDate !== null && {
          deliveryDate: normalizeDeliveryDateToYYYYMMDD(condition.deliveryDate),
        }),
      };
      const doRequest = axios.post(`${API_REQUEST_URL}/getOrderStatusDetail`, payload, {
        headers: {
          'Authorization': token,
        }
      });
      doRequest.then(
        (res) => {
          // Check if response has the nested format (for underMin)
          const isUnderMinResponse = res.data && res.data.result && res.data.totalCount !== undefined;
          
          dispatch({
            type: DASHBOARD_FETCH_ORDER_REPORT_DATA_SUCCESS,
            data: isUnderMinResponse ? res.data.result : res.data,
            totalCount: isUnderMinResponse ? res.data.totalCount : undefined,
            isUnderMin: condition.orderStatus === 'underMin',
          });
          resolve(res.data);
        },
        // Use rejectHandler as the second argument so that render errors won't be caught.
        (err) => {
          const errorMsg = err && err.response ? err.response.data.message : ERROR_CONTACT_ADMIN;
          dispatch({
            type: DASHBOARD_FETCH_ORDER_REPORT_DATA_FAILURE,
            data: { error: errorMsg },
          });
          reject(err);
        },
      );
    });

    return promise;
  };
}

export function dismissFetchOrderReportDataError() {
  return {
    type: DASHBOARD_FETCH_ORDER_REPORT_DATA_DISMISS_ERROR,
  };
}

export function useFetchOrderReportData() {
  const dispatch = useDispatch();

  const { orderReportData, fetchOrderReportDataPending, fetchOrderReportDataError } = useSelector(
    state => ({
      orderReportData: state.dashboard.orderReportData,
      fetchOrderReportDataPending: state.dashboard.fetchOrderReportDataPending,
      fetchOrderReportDataError: state.dashboard.fetchOrderReportDataError,
    }),
    shallowEqual,
  );

  const boundAction = useCallback((...args) => {
    return dispatch(fetchOrderReportData(...args));
  }, [dispatch]);

  const boundDismissError = useCallback(() => {
    return dispatch(dismissFetchOrderReportDataError());
  }, [dispatch]);

  return {
    orderReportData,
    fetchOrderReportData: boundAction,
    fetchOrderReportDataPending,
    fetchOrderReportDataError,
    dismissFetchOrderReportDataError: boundDismissError,
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case DASHBOARD_FETCH_ORDER_REPORT_DATA_BEGIN:
      // Just after a request is sent
      return {
        ...state,
        fetchOrderReportDataPending: true,
        fetchOrderReportDataError: null,
      };

    case DASHBOARD_FETCH_ORDER_REPORT_DATA_SUCCESS:
      // The request is success
      return {
        ...state,
        orderReportData: action.data,
        // Only set totalCount for underMin, otherwise keep existing total from props
        ...(action.isUnderMin && action.totalCount !== undefined && {
          orderReportDataTotalCount: action.totalCount,
        }),
        fetchOrderReportDataPending: false,
        fetchOrderReportDataError: null,
      };

    case DASHBOARD_FETCH_ORDER_REPORT_DATA_FAILURE:
      // The request is failed
      return {
        ...state,
        fetchOrderReportDataPending: false,
        fetchOrderReportDataError: action.data.error,
      };

    case DASHBOARD_FETCH_ORDER_REPORT_DATA_DISMISS_ERROR:
      // Dismiss the request failure error
      return {
        ...state,
        fetchOrderReportDataError: null,
      };

    default:
      return state;
  }
}
